import { Component, ViewEncapsulation, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { Subscription, of, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ActivatedRoute, Params, Router  } from '@angular/router';

import { ProductViewHelperService } from './../../services/product-view-helper.service';
import { AppHelperService } from './../../../app/services/app-helper.service';
import { LoaderService } from 'modules/app/services/loader.service';
import { ContentHelper } from 'libs-content-helper';
import { ProductTocService } from 'modules/shared-postlogin/services/product-toc.service';
import { HtmlMetaManagerService } from '../../../app/services/html-meta-manager.service';
import translations from './../../../../assets/i18n/en';
import { ThemeService } from "libs-frontend-micro-base";
import { ContentAccessHelperService } from "../../services/content-access-helper.service";
import { TestHelperService } from '../../../shared-postlogin/services/test-helper.service';
import { TranslateService } from '@ngx-translate/core';
import { PageTitleService } from 'modules/app/services/page-title.service';
import { configService } from "libs-frontend-micro-base";
import { appSetting } from "../../../../config/appSettings";

@Component({
    selector: 'activity-launch',
    templateUrl: './activity-launch.template.html',
    styleUrls: ['./activity-launch.style.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class ActivityLaunch implements OnInit, OnDestroy {
    public contentHelper = ContentHelper;
    paramsSubscription: Subscription;
    logOutCalledSubscription: Subscription;
    saveAlertActionSubscription: Subscription;
    activityEventsSubscription: Subscription;
    itemid: string;
    toc: any;
    selectedItemData: any;
    selectedItem: any = {};
    bookPath: string;
    productMeta: any;
    classMeta: any;
    saveAlertNeeded: boolean = false; 
    skillSubType = ["cup-pskill", "cup-pskill-group", "cup-collab-task"];
    productCode: string;
    currentScreenNumber: number;
    totalScreenCount: number;
    themeService: any;
    unlockDetail: any = {};
    classEndDate: number;
    showSPAProgressBar: boolean = false;
    isCurrentActivityLocked: boolean = false;
    showLockScreenLoader: boolean = false;
    /* segment primary or secondary*/
    segment: string = '';
    bundleDetails: any;
    @ViewChild('showSaveAlertModalBtn') showSaveAlertModalBtn : ElementRef;
    testTagErrObj: any = {};
    showLtiResultScreen: boolean = false;
    ltik: string;
    ltiGiId: string;
    isLitmusAvailable: boolean = true;
    private readonly subscriptions: Subscription = new Subscription();

    private userRole: string;

    constructor( 
        private route: ActivatedRoute,
        private router: Router,
        public productViewHelperService: ProductViewHelperService,
        private appHelperService: AppHelperService,
        private loaderService: LoaderService,
        private contentAccessHelperService: ContentAccessHelperService,
        private productTocService: ProductTocService,
        private htmlMetaManagerService: HtmlMetaManagerService,
        public testHelperService: TestHelperService,
        private readonly translate: TranslateService,
        private readonly pageTitleService: PageTitleService
        ) { 
            this.themeService = new ThemeService();
        }
    
    ngOnInit() {
        var productTOCData = this.route.parent.snapshot.data.resolveData.productData;

        // If teacher is trying to launch an activity from an assignment which is past the class end date, then navigate them back to class view.
        if (
            this.route.snapshot.data.source === 'TEACHER_CREATE_ASSIGNMENT' &&
            productTOCData?.classmeta?.class?.enddate < productTOCData?.serverTime
        ) {
            const spaceId = this.route.parent.snapshot?.params?.spaceid;
            const classId = this.route.parent.snapshot?.params?.classid;
            this.appHelperService.navigateToClassApp(['teacher', spaceId, 'class', classId, 'view', 'assignments']);
            return;
        }

        this.userRole = this.route.parent.snapshot.data.resolveData.userProfileData.role;
        this.toc = productTOCData.toc;
        this.productMeta = productTOCData.meta.meta;
        this.classMeta = productTOCData.classmeta;
        this.bookPath = productTOCData.meta.meta.paths['public-assets'];
        this.bundleDetails = productTOCData.bundleDetails;

        if(productTOCData.classmeta) {
            this.classEndDate =  productTOCData.classmeta.class.enddate;
        }

        this.isLitmusAvailable = productTOCData.serviceAvailabilityFlags && productTOCData.serviceAvailabilityFlags.litmusAvailable;

        //subscribe to parent route changes to capture change in productCode
        this.subscriptions.add(this.route.parent.params.subscribe((params: Params) => {
            this.productCode = params.productcode;
            this.setPageTitle();
        }));

        this.ltik = this.route.snapshot.queryParams['ltik'];
        this.ltiGiId = this.route.snapshot.queryParams['ltiGiId'];
        // check if this.ltiGiId is of string type and not an array https://3.basecamp.com/4489886/buckets/20201395/messages/7420397369#__recording_7420747741
        if (this.ltiGiId && typeof this.ltiGiId !== 'string') {
            this.appHelperService.navigateToOnboardingApp(['lti-onboarding-error', 'gradebookItemUrlInvalid']);
            return;
        }

        //subscribe to route changes to capture change in itemid
        //and launch new activity
        this.paramsSubscription = this.route.params.subscribe((params: Params) => {
            this.showLtiResultScreen = this.route.snapshot.queryParams['lti_return_status'] === 'submitted';
            this.saveAlertNeeded = false;
            // Navigate to first item, if current itemid is not found in the product
            if (this.toc.navigationMeta.find( item => item['item-code'] === params.itemid) === undefined) {
                const requiredQueryParams = this.appHelperService.getRequiredQueryParams(this.route.snapshot.queryParams, ['lf', 'cid', 'bcode', 'rcode','ltik', 'ltiGiId']);
                return this.router.navigate(['../../item', this.toc.navigationMeta[0]['item-code']],
                    { relativeTo: this.route, replaceUrl: true, queryParams: requiredQueryParams });
            }
            this.setSelectedItem(params.itemid);
            setTimeout(() => {
                this.activateSubTypeExt().catch(e => { throw e; });
            });
        });

        //Get segment style - to display corresponding result screen accordingly
        const classProduct = productTOCData.bundleDetails.products.find((product) => product['dls-product'].code === this.productCode);
        this.segment = classProduct.segment ? classProduct.segment.segment : '';

        // For LPv4 components, the theme is already set beforehand in product-view. So, ignoring LPv4 component activities.
        (classProduct?.['learning-path']?.version !== '4') &&
            this.themeService.activateThemeOnComponent(this.userRole === 'student' && this.segment ? this.segment : 'adult', 'activityLaunch');

        this.productViewHelperService.setSegment(this.segment);
        //Recording alert logout case
        this.logOutCalledSubscription = this.productViewHelperService.logOutCalled.subscribe(()=> {
            if (this.saveAlertActionSubscription) {
                this.saveAlertActionSubscription.unsubscribe();
            }
            // Check if the user can navigate out
            this.saveAlertActionSubscription = this.canDeactivate().subscribe(res => {
                this.saveAlertNeeded = false; //To avoid showing alert again
                if (res) {
                    // If the user wants to exit, proceed
                    this.productViewHelperService.saveAlertActionOnLogout.next();
                }
            });
        })
        // Activity Events for SPA
        this.activityEventsSubscription = this.productViewHelperService.activityEventsSubject.subscribe(({containerEvent, containerEventData})=> {
            switch (containerEvent) {
                case 'currentScreen': {
                    this.currentScreenNumber = containerEventData;
                    return;
                }
                case 'container_controls': {
                    if (containerEventData === 'screenLaunched') {
                        this.showSPAProgressBar = true;
                    }
                    return;
                }
                case 'showExitConfirmationModal': {
                    // Handle exit confirmation modal event from xapi library (scomp-container)
                    this.showExitConfirmationModal(containerEventData);
                    return;
                }
                case 'activityClosed': {
                    // When scomp activity is closed via container (e.g., C1 Header), navigation is handled by the container itself, so, no need to handle navigation here.
                    // When the activity is closed via activity CTOs, we must handle navigation here.
                    if (
                        this.selectedItemData["sub-type"] === "ext-cup-scompscoreable" &&
                        !containerEventData.isContainerCloseCalled
                    ) {
                        // Navigate to learner assignments if a learner is navigating back from an assignment activity
                        if (this.route.snapshot.data?.source === 'LEARNER_ATTEMPT_ASSIGNMENT') {
                            this.appHelperService.navigateToClassApp(['learner', this.route.parent?.snapshot?.params?.spaceid, 'class', this.route.parent?.snapshot?.params?.classid, 'assignments']);
                            return;
                        }
                        this.loaderService.showOverlayLoader();
                        const requiredQueryParams = this.appHelperService.getRequiredQueryParams(this.route.snapshot.queryParams, ['lf', 'cid', 'bcode', 'rcode','ltik', 'ltiGiId']);
                        this.router.navigate(['../../'], { relativeTo: this.route, queryParams: requiredQueryParams });
                        return;
                    }
                    this.showSPAProgressBar = false;
                    return;
                }
                case 'totalScreens': {
                    this.totalScreenCount = containerEventData;
                    return;
                }
            }
        });

    }

    private setSelectedItem (itemid) {
        if (this.productViewHelperService.existsActivityTiming(this.itemid)) {
            this.productViewHelperService.addActivityTiming(this.itemid, 'nextItem');
        }
        this.itemid = decodeURIComponent(itemid);
        let decodedParamsArray = this.itemid.split('/');
        
        /* NEMO-4752 - Code is not generic for n-folder levels */
        if(decodedParamsArray.length === 1) {
            this.selectedItem = {
                ...(decodedParamsArray[0] && {activityId : decodedParamsArray[0]})
            }
        }
        else if(decodedParamsArray.length === 2) {
            this.selectedItem = {
                ...(decodedParamsArray[0] && {lessonId : decodedParamsArray[0]}),
                ...(decodedParamsArray[0] && {activityId : decodedParamsArray[0] + '/' + decodedParamsArray[1]})
            }
        }
        else {
            this.selectedItem = {
                ...(decodedParamsArray[0] && {unitId : decodedParamsArray[0]}),
                ...(decodedParamsArray[1] && {lessonId : decodedParamsArray[0] + '/' + decodedParamsArray[1]}),
                ...(decodedParamsArray[2] && {activityId : decodedParamsArray[0] + '/' + decodedParamsArray[1] + '/' + decodedParamsArray[2]}),
            };
        }

        if (this.selectedItemData) {
            this.showLtiResultScreen = false;
        }

        this.selectedItemData = this.getSelectedItemData(this.toc, this.itemid);
        
        // Get mapped tags from backend and get test type tags.
        const bundleProduct = this.bundleDetails.products.find((product) => product['dls-product'] && this.productCode && product['dls-product'].code === this.productCode);
        this.testTagErrObj = {};
        if (this.productViewHelperService.isTestTypeComponent(bundleProduct)) {
            this.addTestTypeTagToSelectedItem();
        }

        this.productViewHelperService.selectedItemId.next({itemid: this.itemid, selectedItem: this.selectedItem, selectedItemData: this.selectedItemData, showLtiResultScreen: this.showLtiResultScreen});

        this.setSelectedItemData();
    }

    /* Function to get selected item data  */
    private getSelectedItemData(toc, itemId) {
        let activityItems = [];
        let selectedItemData;
        toc.tocArray.result.some((unit, unitIndex) => {
            activityItems = this.productTocService.getLeafNodeItems(unit);
            selectedItemData = activityItems.find((activity) => {
                let itemCode = decodeURIComponent(activity['item-code']);
                return (itemId === itemCode);
            })
            return selectedItemData;
        });
        return selectedItemData;
    }

    updateParentChildLockCount(toc, selectedItemData) {
        const itemCode = selectedItemData['item-code'].split('/');
        const unitItemCode = itemCode[0];
        const lessonItemCode = itemCode[0] + "/" + itemCode[1];

        if (selectedItemData['accessInfo'].isLocked) {
            // increment if new state is changed to lock
            toc.tocObject[unitItemCode]['items'][lessonItemCode]['accessInfo'].childLockCount++;
            toc.tocObject[unitItemCode]['accessInfo'].childLockCount++;
        } else {
            // decrement if new state is changed to unlock
            toc.tocObject[unitItemCode]['items'][lessonItemCode]['accessInfo'].childLockCount--;
            toc.tocObject[unitItemCode]['accessInfo'].childLockCount--;
        }
    }

    private setSelectedItemData () {
        if (ContentHelper.isDownloadableContent(this.selectedItemData)) {
            var subType = this.selectedItemData["sub-type"];
            var downloadUrl = this.appHelperService.isAbsoluteUrl(this.selectedItemData[subType].url) ? this.selectedItemData[subType].url : (this.bookPath + '/' + this.selectedItemData[subType].url);
            this.selectedItemData.downloadUrl = downloadUrl;
            this.selectedItemData.filename = this.selectedItemData[subType].filename;
        } else if (this.skillSubType.indexOf(this.selectedItemData["sub-type"]) > -1){
            //productive-skill component auto handles
            //add itemid to 'pskill' input to be used in productive-skill component
            this.selectedItemData[this.selectedItemData["sub-type"]]['cup-options'].itemid = this.itemid;
            //this.selectedItemData[]
        }
    }

    /**
     * Calculate the analytics across all SComp activities in the product
     * @returns Object containing completionCount and timespent for SComp analytics
     */
    private getSCompAnalytics() {
        let completionCount = 0;
        let timespent = 0;

        // Iterate through all items in the TOC
        for (const item of this.toc.navigationMeta) {
            if (item['sub-type'] === 'ext-cup-scompscoreable' && item.__analytics) {
                completionCount += item.__analytics.completedCountOverall || 0;
                timespent += item.__analytics.timespent || 0;
            }
        }

        return {
            completionCount,
            timespent
        };
    }

    private async activateSubTypeExt() {
        const itemData = this.selectedItemData;

        if(this.contentAccessHelperService.getContentAccessEnabled()) {
            const previousActivityState = itemData['accessInfo'].isLocked;

            const activeRule = this.contentAccessHelperService.getCurrentActiveRule(itemData.accessInfo.finalResultantLockDetails, itemData['item-code'], this.productViewHelperService.getIsAssignmentOnlyProduct());
            itemData['accessInfo']['isLocked'] = activeRule.isLocked;
            this.unlockDetail = activeRule.unlockRuleDetails;

            // State change means increment or decrement child lock count
            if (itemData['accessInfo'].isLocked !== previousActivityState) {
                this.updateParentChildLockCount(this.toc, itemData);
            }

            if (itemData['accessInfo'].isLocked) {
                this.isCurrentActivityLocked = true;
                this.showLockScreenLoader = true;
                setTimeout(()=>{
                    this.showLockScreenLoader = false;
                }, 100);
                this.loaderService.hideLoader();
                return;
            }
        }

        this.isCurrentActivityLocked = false;
        const contentPath = this.bookPath + '/' + itemData[itemData["sub-type"]]['url'];
        const contentType = itemData["sub-type"];
        const initParams: any = {
            learningObject: {
                path: contentPath,
                contentType: contentType,
                code: itemData['item-code'],
                name: itemData['item-code'],
                meta: { contributesToCompletion: itemData['contributes-to-completion'] },
                iframeTitle: this.translate.instant("LEARNER.ACTIVITYLAUNCH.SCREEN_READER.ACTIVITY", {activityName: itemData['name']})
            }
        };
        // For pskill type or ext-cup-xapiscoreable type activities send ltiGiId to xapi runner in ltiGradebookItemId
        if((this.skillSubType.indexOf(contentType) > -1 || contentType === "ext-cup-xapiscoreable" || contentType === "generic-lti")  && this.ltiGiId) {
            initParams.learningObject.ltiPlatform = {
                "ltiGradebookItemId": this.ltiGiId
            }
        }
        if (contentType === "ext-cup-xapi" || contentType === "ext-cup-xapiscoreable") {
            var containerType = 'avallain-opener';
            if (itemData[contentType]['container'] === 'Channel') {
                containerType = 'generic-content';
            } else if (itemData[contentType]['container'] === 'spa-container') {
                containerType = 'spa-container';
            }
            initParams.container = {
                type: containerType,
                config: {
                    disableLOButtons: true,
                }
            };
            // Enable Walkthrough for spa-container
            if (containerType === 'spa-container') initParams.container.config.enableWalkthrough = true;
            //param to show c1 result screen for generic scorable
            if (ContentHelper.containerResultScreen(itemData)) {
                initParams.container.config.loExternalResultScreen = true;
            }

            if (containerType === 'avallain-opener' || containerType === 'generic-content') {
                if (itemData[contentType]["cup-options"]) {
                    initParams.container.config.paths = {
                        engine: itemData[contentType]["cup-options"].engine,
                        dp: itemData[contentType]["cup-options"].dp
                    }
                }
            }
            // __analytics node won't exist for teacher hence we won't call getState for teacher
            if (itemData.__analytics && itemData.__analytics.status === "in_complete") {
                const response = await this.productViewHelperService.getXAPIAnalyticsObject().getState(itemData['item-code']);
                initParams.learningObject.state = response;
            } else {
                initParams.learningObject.status = "completed"; // By default set status as "completed" (for teachers)
                if (itemData.__analytics) {
                    // For learner, pass the value of status from analytics node
                    initParams.learningObject.status = itemData.__analytics.status;
                }
            }
            this.productViewHelperService.getXAPIRunnerObject().paintActivity(itemData['item-code'], "content-course-ext", initParams);
        } else if (contentType === "ext-cup-html") {
            this.productViewHelperService.getXAPIRunnerObject().paintActivity(itemData['item-code'], "content-course-ext", initParams);
            this.productViewHelperService.closeActivityBtnSubject.next({ visible: true });
        } else if (ContentHelper.isDownloadableContent(itemData)) {
            initParams.container = {
                type: 'nopack-content',
                config: {
                    trackLaunch: true,
                    trackClose: true,
                    trackTime: false,
                    trackComplete: true
                }
            }

            this.productViewHelperService.getXAPIRunnerObject().paintActivity(itemData['item-code'], "content-course-ext", initParams);
            this.productViewHelperService.closeActivityBtnSubject.next({ visible: true });
        } else if (this.skillSubType.indexOf(contentType) > -1) {
            initParams.container = {
                type: 'nopack-content',
                config: {
                    trackLaunch: contentType == "cup-collab-task" ? true : false,
                    trackClose: true,
                    trackTime: true,
                    trackComplete: false
                }
            }
            this.productViewHelperService.getXAPIRunnerObject().paintActivity(itemData['item-code'], "content-course-ext", initParams);
        } else if (contentType === "ext-cup-scompscoreable") {
            initParams.container = {
                type: 'scomp-container',
                config: {
                    trackTime: false,
                    areAttemptsExhausted: false,
                    isPartOfAssignmentOrAccessRule: ['TEACHER_CREATE_ASSIGNMENT', 'TEACHER_EDIT_ASSIGNMENT', 'TEACHER_VIEW_ASSIGNMENT', 'LEARNER_ATTEMPT_ASSIGNMENT', 'CREATE_ACCESS_RULE'].includes(this.route.snapshot.data?.source),
                    isCreateAssignmentFlow: this.route.snapshot.data?.source === 'TEACHER_CREATE_ASSIGNMENT',
                    disableLOButtons: true,
                    c1HelpUrl: configService.getHelpPagePath(APP_ENV, 'requests', '', localStorage.getItem(appSetting.localStorage.lang) ?? 'en')
                }
            };

            if (this.userRole === 'student') {
                initParams.container.config.analytics = this.getSCompAnalytics();
            }

            initParams.learningObject.launchUrl = itemData[contentType]['launch-url'];
            this.productViewHelperService.getXAPIRunnerObject().paintActivity(itemData['item-code'], "content-course-ext", initParams);
        }
        this.productViewHelperService.setLOInitParams(initParams);
        this.loaderService.hideLoader();
    }
    
    downloadActivity() {
        this.productViewHelperService.getXAPIRunnerObject().generateCustomStatement(this.itemid, "downloaded");
    }

    public canDeactivate() {
        const itemData = this.selectedItemData;
        // Assume that the activity needs to be closed
        let returnObservable =  of(true);

        const state = this.router.getCurrentNavigation()?.extras?.state;

        if (
            (itemData?.['sub-type'] === 'ext-cup-scompscoreable' && state?.navigationTrigger === 'cup-logo' && !this.isCurrentActivityLocked) ||
            (!this.productViewHelperService.getIsPreviewMode() && itemData && ContentHelper.isSubmittableContent(itemData) && this.saveAlertNeeded)
        ) {
            // Show save alert
            this.loaderService.hideLoader();
            this.showSaveAlertModalBtn.nativeElement.click();
            // Save alert component will generate true if the user wants to leave the activity
            returnObservable = this.productViewHelperService.saveAlertAction;
        }
    
        return returnObservable.pipe(
            switchMap(closeActivity => {
                // Handle closing of activity
                if (closeActivity) {
                    if (itemData?.accessInfo && !itemData['accessInfo'].isLocked) {
                        return from<any>(this.productViewHelperService.getXAPIRunnerObject().closeLO(itemData['item-code']).then(()=> {
                            if (this.contentHelper.isDownloadableContent(itemData)) {
                                this.productViewHelperService.closeActivityBtnSubject.next({ visible: false });
                            }
                            return true;
                        }));
                    }
                }
                return of(closeActivity);
            })
        );
    }

    isSaveAlertNeeded(val) {
        this.saveAlertNeeded = val;
    }

    /**
     * Handles the showExitConfirmationModal event from xapi library (scomp-container)
     * Opens the save-alert modal for user confirmation
     */
    private showExitConfirmationModal(eventData: any) {        
        // Check if this is a scomp activity
        if (this.selectedItemData?.['sub-type'] !== 'ext-cup-scompscoreable') {
            return;
        }

        this.showSaveAlertModalBtn.nativeElement.click();

        if (eventData.isVisible) {
            const subscription = this.productViewHelperService.saveAlertAction.subscribe((value) => {
                subscription.unsubscribe();

                if (!value) {
                    return this.productViewHelperService.getXAPIRunnerObject().containerInvocation(
                        this.selectedItemData['item-code'],
                        'generic_controls',
                        { type: 'exitConversation', data: {value: false} }
                    );
                };

                this.productViewHelperService.getXAPIRunnerObject().containerInvocation(
                    this.selectedItemData['item-code'],
                    'generic_controls',
                    { type: 'exitConversation', data: {value: true} }
                );
            });
            this.subscriptions.add(subscription);
        }
    }
    
    private setPageTitle(){ 
        let titlePrefix;
        let titleDesc;
        switch(this.route.snapshot.data.source){
            case 'TEACHER_CREATE_ASSIGNMENT':
                if(this.route.snapshot?.queryParams?.ltik) {
                    titlePrefix = translations.HTML_META.CREATE_CUSTOM_LINK.TITLE + ' ' + this.productMeta?.title + ' - ' + this.bundleDetails.title;
                    titleDesc = translations.HTML_META.CREATE_CUSTOM_LINK.DESCRIPTION;
                } else {
                    titlePrefix = translations.HTML_META.CREATE_ASSIGNMENT.TITLE + ' - ' + this.bundleDetails.title;
                    titleDesc = translations.HTML_META.CREATE_ASSIGNMENT.DESCRIPTION;
                }
                break;
            case 'TEACHER_EDIT_ASSIGNMENT':
                titlePrefix = translations.HTML_META.EDIT_ASSIGNMENT.TITLE;
                titleDesc = translations.HTML_META.EDIT_ASSIGNMENT.DESCRIPTION;
                break;
            default: //LEARNER || 'TEACHER_VIEW_ASSIGNMENT' || 'TEACHER_REVIEW_TEST'
                titlePrefix = this.bundleDetails.title;
                titleDesc = translations.HTML_META.LEARNERLEARNINGPATH.DESCRIPTION;
                break;
        }
        this.htmlMetaManagerService.setTitle(translations.HTML_META.LEARNERLEARNINGPATH.TITLE, titlePrefix);
        this.htmlMetaManagerService.setDescription(titleDesc);
        if(this.route.snapshot.data.source === 'TEACHER_CREATE_ASSIGNMENT' && this.route.snapshot?.queryParams?.ltik) {
            this.pageTitleService.setPageTitleToSROnlyDiv(document.title);   
        }
    }

    // Function to update error message for test type tags.
    private updateErrorMsgForTestTypeTags(testType) {
        if (!testType) {
            this.testTagErrObj.errorMsg = 'LEARNER.ACTIVITYLAUNCH.NO_TEST_TYPE_ERROR';
        }
    }

    // Function to add test type tag to selected item.
    private addTestTypeTagToSelectedItem() {
        let testType;
        if (this.selectedItemData && this.selectedItemData.model && this.selectedItemData.model['test-type']) {
            testType = this.selectedItemData.model['test-type'];
        }

        // in case of pskill, do not display error msg regarding tags.
        if(!ContentHelper.isSubmittableContent(this.selectedItemData)){
            this.updateErrorMsgForTestTypeTags(testType);
        }
        
        if (!this.testTagErrObj.errorMsg) {
            this.selectedItemData.testTypeTag = testType;
        }
    }

    /**
     * Navigate back from locked scomp activity
     * Removes item and itemcode from the URL
     */
    navigateBackFromLockedActivity() {
        const requiredQueryParams = this.appHelperService.getRequiredQueryParams(this.route.snapshot.queryParams, ['lf', 'cid', 'bcode', 'rcode','ltik', 'ltiGiId']);
        this.router.navigate(['../../'], { relativeTo: this.route, queryParams: requiredQueryParams });
    }

    ngOnDestroy() {
        this.pageTitleService.setPageTitleToSROnlyDiv('');   
        if(this.paramsSubscription){
            this.paramsSubscription.unsubscribe();
        }
        if(this.logOutCalledSubscription) {
            this.logOutCalledSubscription.unsubscribe();
        }
        if(this.activityEventsSubscription) {
            this.activityEventsSubscription.unsubscribe();
        }

        this.subscriptions.unsubscribe();
    }
}
